import { ErrorBoundary } from "ErrorBoundary"
import { Map as LeafletMap } from "leaflet"
import { useFetchVesselPositions } from "modules/PortPredictor/hooks/useFetchVesselPositions"
import { useGetVessels } from "modules/VesselSearch/useGetVessels"
import { useCallback, useEffect, useReducer, useRef, useState } from "react"
import { Button, Card } from "react-bootstrap"
import { useSelector } from "react-redux"
import { RootState } from "store"
import { getFromLocalStorage } from "user/userSlice"
import {
  VesselSearch,
  VesselSegmentInputSelection,
} from "../../VesselSearch/VesselSearchMain"
import { formatVesselNamesForDropdown } from "../helpers/formatVesselNamesForDropdown"
import { getMarkerPointsFromJourneyMarkerState } from "../helpers/mapHelpers"
import { useItineraryContext } from "../hooks/itineraryContext"
import { useFetchFuturePath } from "../hooks/useFetchFuturePath"
import { useFetchVesselDetails } from "../hooks/useFetchVesselDetails"
import { useFetchVesselJourneySpeed } from "../hooks/useFetchVesselJourneySpeed"
import { useFetchVesselState } from "../hooks/useFetchVesselJourneyStart"
import { useFetchVesselStays } from "../hooks/useFetchVesselStays"
import { Markers } from "../hooks/useItineraryAPI"
import { createJourneyStart, useJourneyStart } from "../hooks/useJourneyStart"
import {
  ADD_JOURNEY_START_MARKERS,
  EDIT_JOURNEY,
  EXTEND_JOURNEY_MARKERS,
  PortPredictorJourneyMarker,
  portPredictorJourneyReducer,
} from "../portPredictorReducer"
import { ErrorFallback } from "./PortPredictorErrorFallback"
import { capitalizeFirstLetter } from "./PortPredictorItinerary/Utilities"
import { PortPredictorMap } from "./PortPredictorMap"
import { PortPredictorSpeedGraphModal } from "./PortPredictorSpeedGraphModal"
import { PortPredictorTimeline } from "./PortPredictorTimeline/PortPredictorTimeline"
import { VesselSegment } from "user/userService"
import Select from "react-select"
import { SingleValue } from "react-select"
import {
  getStandardVesselDefaultValue,
  getStandardVesselSpeed,
  standardVesselOptions,
} from "./PortPredictorMainHelpers"
import { getVesselSpeedDataWithAverages } from "./PortPredictorDeparture/helpers"

export interface VesselListSingle {
  imo: number
  segment: string
  vessel_name: string
}

export interface MarkerPoint {
  journeyMarkerStateIndex?: number
  journeyMarkerStateLocation?: string
  port?: string
  lat: number
  lon: number
}

export interface MarkerLatLon {
  lat: number
  lon: number
  markerId?: number
}

export function PortPredictor({
  updatePortPredictorItineraryMarkers,
  initialSavedMarkers,
  selectedId,
  keyState,
}: {
  updatePortPredictorItineraryMarkers: (
    itineraryMarkers: Markers,
    selectedId: number
  ) => void
  initialSavedMarkers: Markers | undefined
  selectedId: number
  keyState: string
}) {
  const { getTabStateKeyValue, itineraryState } = useItineraryContext()

  /**
   * Itinerary sections
   */

  const selectedIdRef = useRef(selectedId)

  /**
   * Segment
   */
  const user = useSelector((state: RootState) => state.user)

  const vesselSegments = user.chosenOrganization?.segments

  const defaultSegment = user.defaultSegment ? user.defaultSegment : undefined

  const [chosenSegment, setChosenSegment] =
    useState<VesselSegmentInputSelection>(
      defaultSegment
        ? {
            label: capitalizeFirstLetter(getFromLocalStorage("defaultSegment")),
            value: getFromLocalStorage("defaultSegment"),
          }
        : { label: "panamax", value: VesselSegment.Panamax }
    )

  const [realVesselSegment, setRealVesselSegment] =
    useState<VesselSegmentInputSelection | null>(chosenSegment)

  const [standardVesselSegment, setStandardVesselSegment] =
    useState<VesselSegmentInputSelection | null>(null)

  /**
   * Chosen Vessel
   */
  const [chosenVesselImo, setChosenVesselImo] = useState<number | undefined>()
  const [chosenVesselName, setChosenVesselName] = useState<string | undefined>()
  const { vesselsData, getVessels, getVesselsLoading, clearVesselsData } =
    useGetVessels({
      defaultSegment: realVesselSegment?.value || defaultSegment || undefined,
    })

  const {
    isChosenVesselStateLoading,
    chosenVesselStateData,
    isChosenVesselStateSuccess,
  } = useFetchVesselState(
    chosenVesselImo,
    initialSavedMarkers?.meta?.data &&
      "chosenVesselStateData" in initialSavedMarkers?.meta?.data
      ? initialSavedMarkers?.meta?.data["chosenVesselStateData"]
      : null
  )

  const { isChosenVesselSpeedSuccess, chosenVesselSpeedData } =
    useFetchVesselJourneySpeed(
      chosenVesselImo,
      chosenSegment.value,
      initialSavedMarkers?.meta?.data &&
        "chosenVesselSpeedData" in initialSavedMarkers?.meta?.data
        ? initialSavedMarkers?.meta?.data["chosenVesselSpeedData"]
        : null
    )

  /**
   * Journey
   */
  const [journeyMarkerState, journeyMarkerDispatch] = useReducer(
    portPredictorJourneyReducer,
    []
  )

  const addJourneyStartMarkersArr = useCallback(
    (markers: PortPredictorJourneyMarker[]): void => {
      journeyMarkerDispatch({
        type: ADD_JOURNEY_START_MARKERS,
        payload: markers,
      })
    },
    [journeyMarkerDispatch]
  )

  const { journeyStartMarkersArr } = useJourneyStart({
    chosenVesselStateData,
    chosenVesselImo,
    journeyMarkerState,
    chosenVesselSpeedData,
    isChosenVesselSpeedSuccess,
    isChosenVesselStateSuccess,
    addJourneyStartMarkersArr,
    isStartOfJourney: journeyMarkerState.length === 0,
  })

  const dispatchEditJourney = useCallback(
    (markers: PortPredictorJourneyMarker[]): void => {
      journeyMarkerDispatch({
        type: EDIT_JOURNEY,
        payload: markers,
      })
    },
    []
  )
  const dispatchAddJourneyStartMarkers = useCallback(
    (markers: PortPredictorJourneyMarker[]): void => {
      journeyMarkerDispatch({
        type: ADD_JOURNEY_START_MARKERS,
        payload: markers,
      })
    },
    []
  )
  const dispatchExtendJourneyMarkers = useCallback(
    (markers: PortPredictorJourneyMarker[]): void => {
      journeyMarkerDispatch({
        type: EXTEND_JOURNEY_MARKERS,
        payload: markers,
      })
    },
    []
  )

  /**
   * Itinerary
   */

  /**
   * Chosen Vessel
   */
  const curPrefferedSegment = useRef(chosenSegment)
  useEffect(() => {
    let curSegment = chosenSegment
    let curVesselImo = chosenVesselImo
    let curVesselName = chosenVesselName
    if (selectedIdRef.current !== selectedId) {
      curVesselImo = undefined
      curVesselName = undefined

      selectedIdRef.current = selectedId
    }

    if (
      !initialSavedMarkers ||
      !(initialSavedMarkers.meta && initialSavedMarkers.data)
    ) {
      setChosenSegment(curSegment)
      setChosenVesselImo(curVesselImo)
      setChosenVesselName(curVesselName)
    } else {
      // Check for saved metadata
      if (initialSavedMarkers.meta) {
        // Check for saved segment
        if (initialSavedMarkers.meta.segment) {
          const savedSegment = initialSavedMarkers.meta.segment
          setChosenSegment(
            savedSegment.value
              ? savedSegment
              : ({
                  label: defaultSegment,
                  value: defaultSegment,
                } as VesselSegmentInputSelection)
          )
          getVessels({
            segment: savedSegment.value ? savedSegment.value : defaultSegment!,
          })
        } else {
          setChosenSegment(curSegment)
        }

        // Check for saved Vessel (IMO and Name)
        if (initialSavedMarkers.meta.vessel) {
          const savedVessel = initialSavedMarkers.meta.vessel
          if (savedVessel.value) {
            setChosenVesselImo(parseInt(savedVessel.value))
          } else {
            setChosenVesselImo(curVesselImo)
          }

          if (savedVessel.label) {
            setChosenVesselName(savedVessel.label)
          } else {
            setChosenVesselName(curVesselName)
          }
        } else {
          setChosenVesselImo(curVesselImo)
          setChosenVesselName(curVesselName)
        }
      }
      // check for saved markers
      if (
        initialSavedMarkers.data &&
        (initialSavedMarkers.data.length > 0 || getTabStateKeyValue("isNew"))
      ) {
        journeyMarkerDispatch({
          type: "INITIALIZE_JOURNEY_MARKER",
          payload: initialSavedMarkers.data,
        })
      }

      if (curSegment && curPrefferedSegment.current !== curSegment) {
        getVessels({
          segment: curSegment.value ? curSegment.value : defaultSegment!,
        })
        curPrefferedSegment.current = curSegment
      }
    }
  }, [initialSavedMarkers, keyState, selectedId])

  useEffect(() => {
    const markers: Markers = {
      data: [...journeyMarkerState],
      meta: {
        organization: user.chosenOrganization?.name || "",
        segment: chosenSegment!,
        vessel: { label: chosenVesselName || "", value: chosenVesselImo! + "" },
        data: {
          chosenVesselStateData,
          chosenVesselSpeedData,
        },
      },
    }
    updatePortPredictorItineraryMarkers(markers, selectedId)
  }, [
    journeyMarkerState,
    selectedId,
    chosenSegment,
    keyState,
    chosenVesselImo,
    chosenVesselStateData,
    chosenVesselSpeedData,
    chosenVesselName,
    user.chosenOrganization?.name,
  ])

  /**
   * SpeedGraph
   */
  const [isSpeedGraphModalShown, setIsSpeedGraphModalShown] = useState(false)

  /**
   * Map
   */
  const [map, setMap] = useState<LeafletMap | null>(null)
  const { vesselDetailsData } = useFetchVesselDetails(chosenVesselImo)
  const { vesselStaysData } = useFetchVesselStays({
    imo: chosenVesselImo,
    built: vesselDetailsData?.built,
  })

  const { futurePathData } = useFetchFuturePath(
    getMarkerPointsFromJourneyMarkerState(journeyMarkerState)
  )
  const { vesselPositions } = useFetchVesselPositions({
    imo: chosenVesselImo,
    months: 6,
  })

  return (
    <>
      {chosenVesselSpeedData && chosenVesselName && isSpeedGraphModalShown && (
        <PortPredictorSpeedGraphModal
          isSpeedGraphModalShown={isSpeedGraphModalShown}
          chosenVesselSpeedData={chosenVesselSpeedData}
          chosenVesselName={chosenVesselName}
          closeSpeedGraphModal={() => {
            setIsSpeedGraphModalShown(false)
          }}
        />
      )}
      <Card>
        <Card.Body>
          <div className="VesselSearch_Row">
            {defaultSegment && chosenSegment && vesselSegments && (
              <VesselSearch
                vesselsData={formatVesselNamesForDropdown(vesselsData)}
                setRealVesselSegment={(segment) => {
                  setRealVesselSegment(segment)
                }}
                realVesselSegment={realVesselSegment}
                getVesselsLoading={getVesselsLoading}
                getChosenVesselLoading={isChosenVesselStateLoading}
                getChosenVessel={({
                  name,
                  imo,
                }: {
                  imo: number
                  name: string
                }) => {
                  journeyMarkerDispatch({ type: "CLEAR_JOURNEY" })
                  setChosenVesselName(name)
                  setChosenVesselImo(imo) // Set on main component
                }}
                setChosenSegment={(segment: VesselSegmentInputSelection) => {
                  journeyMarkerDispatch({ type: "CLEAR_JOURNEY" })
                  setChosenSegment(segment)
                  setStandardVesselSegment(null)
                }}
                getVessels={getVessels}
                vesselSegments={vesselSegments}
                defaultVessel={{
                  label: chosenVesselName || "",
                  value: chosenVesselImo ? chosenVesselImo?.toString() : "",
                }}
                onClickReset={() => {
                  // This works by updating the reducer state, thus triggering a re-render
                  // The re-render then triggers the component to rebuild the timeline
                  journeyMarkerDispatch({ type: "CLEAR_JOURNEY" })
                }}
              />
            )}
            <div className="VesselSearch_Col w-100 VesselSearch_Col--right d-flex flex-column ">
              <div className="d-flex pb-2">
                <h5 className="mb-0 VesselSearch_Col VesselSearch_Title">
                  Standard Vessel
                </h5>
                <Button
                  className="VesselSearch_Button VesselSearch_Col"
                  onClick={() => {
                    if (!standardVesselSegment) {
                      return
                    }
                    journeyMarkerDispatch({ type: "CLEAR_JOURNEY" })
                    const standardVesselSpeed = getStandardVesselSpeed(
                      standardVesselSegment.value
                    )
                    const speedDataWithAvg =
                      getVesselSpeedDataWithAverages(standardVesselSpeed)

                    const markers = createJourneyStart({
                      isStandardVessel: true,
                      speedDataWithAvg,
                    })
                    if (markers) {
                      addJourneyStartMarkersArr(
                        markers as PortPredictorJourneyMarker[]
                      )
                    }
                  }}
                  color="primary"
                >
                  Reset
                </Button>
              </div>
              <Select
                className="w-100"
                isDisabled={!getTabStateKeyValue("isEditMode")}
                defaultValue={
                  defaultSegment
                    ? getStandardVesselDefaultValue(defaultSegment)
                    : null
                }
                placeholder="Select Standard Vessel..."
                options={standardVesselOptions}
                value={standardVesselSegment}
                onChange={(
                  segment: SingleValue<VesselSegmentInputSelection>
                ) => {
                  if (!segment) {
                    return
                  }
                  journeyMarkerDispatch({ type: "CLEAR_JOURNEY" })
                  setChosenVesselImo(undefined)
                  setChosenVesselName(undefined)

                  setStandardVesselSegment({
                    label: segment.label,
                    value: segment.value,
                  })
                  setChosenSegment({
                    label: segment.label,
                    value: segment.value,
                  })
                  setRealVesselSegment(null)
                  clearVesselsData()
                  const standardVesselSpeed = getStandardVesselSpeed(
                    segment.value
                  )
                  const speedDataWithAvg =
                    getVesselSpeedDataWithAverages(standardVesselSpeed)

                  const markers = createJourneyStart({
                    isStandardVessel: true,
                    speedDataWithAvg,
                  })
                  if (markers) {
                    addJourneyStartMarkersArr(
                      markers as PortPredictorJourneyMarker[]
                    )
                  }
                }}
              />
            </div>
          </div>
          <ErrorBoundary FallbackComponent={ErrorFallback}>
            <ErrorBoundary FallbackComponent={ErrorFallback}>
              <>
                <PortPredictorMap
                  chosenVesselStateData={chosenVesselStateData}
                  futurePath={futurePathData}
                  journeyMarkerState={journeyMarkerState}
                  vesselStaysData={vesselStaysData}
                  map={map}
                  setMap={setMap}
                  vesselPositions={vesselPositions}
                />
              </>
            </ErrorBoundary>
            {(chosenSegment && chosenVesselStateData) ||
            (chosenSegment &&
              journeyMarkerState &&
              journeyMarkerState.length > 0) ? (
              <PortPredictorTimeline
                chosenVesselStateData={chosenVesselStateData}
                showSpeedGraphModal={() => {
                  setIsSpeedGraphModalShown(true)
                }}
                chosenVesselSpeedData={chosenVesselSpeedData}
                journeyMarkerState={journeyMarkerState}
                dispatchEditJourney={dispatchEditJourney}
                dispatchExtendJourneyMarkers={dispatchExtendJourneyMarkers}
                dispatchAddJourneyStartMarkers={dispatchAddJourneyStartMarkers}
                segment={
                  chosenSegment.value ? chosenSegment.value : defaultSegment!
                }
                journeyStartMarkersArr={journeyStartMarkersArr}
              />
            ) : (
              ""
            )}
          </ErrorBoundary>
        </Card.Body>
      </Card>
    </>
  )
}
